// The plan and leaving it: the state « Solde et forfait » shows, and the cancellation path (the owner's wish of
// 26/10/2026: an offboarding thought through, a discreet way out, a person at hand, nothing forced).
// No Nuxt dependency: tested with Node alone. The texts live in lang/*.json, under `billing`.

// What the plan line says: a trial and its end, a renewal, a cancellation planned, a plan the team gave.
// `summary`: GET /accounts/:id/subscription (composables/useSubscription.js).
export const planState = (summary) => {
  if (!summary?.plan) return { key: "none", date: null };
  if (summary.endsAt) return { key: summary.status === "trialing" ? "trialEnds" : "ends", date: summary.endsAt };
  if (summary.status === "trialing") return { key: "trial", date: summary.trialEndsAt };
  if (summary.status === "past_due") return { key: "pastDue", date: null };
  if (summary.billing === "manual") return { key: "manual", date: null };
  return { key: "renews", date: summary.renewsAt };
};

// The reasons offered, in this order (the API's codes, services/accounts/cancellation.js), and what the path answers
// to each before the confirmation: a cheaper plan when there is one, a person who calls back, an answer in writing, or
// simply the next step. `comment`: the reasons whose detail the team needs, asked as a question.
export const CANCELLATION_REASONS = [
  { key: "too_expensive", icon: "i-heroicons-banknotes", answer: "cheaperPlan" },
  { key: "too_complex", icon: "i-heroicons-wrench-screwdriver", answer: "callback" },
  { key: "missing_features", icon: "i-heroicons-puzzle-piece", answer: "write", comment: true },
  { key: "unused", icon: "i-heroicons-pause-circle", answer: "none" },
  { key: "switched_service", icon: "i-heroicons-arrows-right-left", answer: "callback", comment: true },
  { key: "low_quality", icon: "i-heroicons-hand-thumb-down", answer: "callback", comment: true },
  { key: "other", icon: "i-heroicons-ellipsis-horizontal", answer: "callback", comment: true }
];

export const reasonOf = (key) => CANCELLATION_REASONS.find((reason) => reason.key === key) ?? null;

// The cheapest paid plan below the current one, or null: « trop cher » answered with what exists, never a discount
// nobody decided.
export const cheaperPlan = (plans, currentId) => {
  const current = (plans ?? []).find((plan) => plan.id === currentId);
  if (!current) return null;
  return (plans ?? [])
    .filter((plan) => Number(plan.price) > 0 && Number(plan.price) < Number(current.price))
    .sort((a, b) => Number(a.price) - Number(b.price))[0] ?? null;
};

// What the path answers to a reason: `cheaperPlan` falls back on a callback when no plan is cheaper.
export const retentionFor = (reasonKey, { plans = [], currentId = null } = {}) => {
  const reason = reasonOf(reasonKey);
  if (!reason) return { kind: "none" };
  if (reason.answer === "cheaperPlan") {
    const plan = cheaperPlan(plans, currentId);
    return plan ? { kind: "cheaperPlan", plan } : { kind: "callback" };
  }
  return { kind: reason.answer };
};

// The topic of a question the path sends to the team (composables/useHelpDesk.js): the plan.
export const OFFBOARDING_HELP_TOPIC = "offer";

// A date as people read it in their language (« 26 novembre 2026 », "November 26, 2026"), or "" without one.
export const longDate = (iso, locale = "fr") => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }).format(date);
};
